import { useState } from 'react';

import { useReportFailure } from './useReportFailure';
import { CONFLICT_MESSAGE_KEYS, readConflictCode } from '../utils/saveConflict';

import type { CompensationDraftRequest, CompensationEnvelope } from '@/types/compensation';

import {
    useCloseCompensationEnvelope,
    useDeleteCompensationEnvelope,
    useExportCompensationEnvelope,
    useReopenCompensationEnvelope,
    useSaveCompensationEnvelope,
} from '@/api/compensation';
import { useAmelioTranslations } from '@/hooks';
import { showErrorToast, showSuccessToast } from '@/plugins/Toast';
import { downloadFile } from '@/utils/fileDownload';

interface UseEnvelopeActionsParams {
    envelope: CompensationEnvelope;
    onDeleted: () => void;
    /** Fetches the envelope again, dropping the draft, after someone else saved it first. */
    onReload: () => void;
}

/** What can be done to the open envelope as a whole: save its draft, delete, close or reopen it, and
 * export its salary list. */
export const useEnvelopeActions = ({ envelope, onDeleted, onReload }: UseEnvelopeActionsParams) => {
    const { t } = useAmelioTranslations();
    const reportFailure = useReportFailure();

    const [nameError, setNameError] = useState<string | undefined>(undefined);

    const { mutateAsync: saveEnvelope, isPending: isSaving } = useSaveCompensationEnvelope();
    const { mutateAsync: deleteEnvelope, isPending: isDeleting } = useDeleteCompensationEnvelope();
    const { mutateAsync: closeEnvelope, isPending: isClosing } = useCloseCompensationEnvelope();
    const { mutateAsync: reopenEnvelope, isPending: isReopening } = useReopenCompensationEnvelope();
    const { mutateAsync: exportEnvelope, isPending: isExporting } = useExportCompensationEnvelope();

    const handleSaveFailure = (error: unknown) => {
        const code = readConflictCode(error);

        if (code === 'name') {
            setNameError(t('compensation.errors.nameConflict'));
            return;
        }

        if (code === 'version') {
            showErrorToast({
                title: t('errors.genericTitle'),
                message: t('compensation.errors.versionConflict'),
                action: { label: t('compensation.errors.reload'), onClick: onReload },
            });
            return;
        }

        if (code === 'closed' || code === 'audience') {
            showErrorToast({
                title: t('errors.genericTitle'),
                message: t(CONFLICT_MESSAGE_KEYS[code]),
            });
            return;
        }

        reportFailure(error, 'compensation.errors.saveFailed');
    };

    const save = async (draftRequest: CompensationDraftRequest) => {
        setNameError(undefined);

        try {
            await saveEnvelope({
                id: envelope.id,
                request: { ...draftRequest, expectedVersion: envelope.version },
            });
            showSuccessToast({ message: t('compensation.saveBar.savedMessage') });
        } catch (error) {
            handleSaveFailure(error);
        }
    };

    const remove = async () => {
        try {
            await deleteEnvelope({
                id: envelope.id,
                cycleEvaluationId: envelope.cycleEvaluationId,
            });
            onDeleted();
            showSuccessToast({ message: t('compensation.delete.success') });
        } catch (error) {
            reportFailure(error, 'compensation.delete.error');
        }
    };

    const toggleStatus = async () => {
        const isClosed = envelope.status === 'Closed';

        try {
            await (isClosed ? reopenEnvelope(envelope.id) : closeEnvelope(envelope.id));
        } catch (error) {
            reportFailure(
                error,
                isClosed ? 'compensation.errors.reopenFailed' : 'compensation.errors.closeFailed',
            );
        }
    };

    const exportSalaries = async () => {
        try {
            const blob = await exportEnvelope(envelope.id);
            downloadFile(blob, `${t('compensation.export.fileNameBase')}.csv`);
        } catch (error) {
            reportFailure(error, 'compensation.errors.exportFailed');
        }
    };

    return {
        nameError,
        save,
        isSaving,
        remove,
        isDeleting,
        toggleStatus,
        isTogglingStatus: isClosing || isReopening,
        exportSalaries,
        isExporting,
    };
};
